import React, { useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { createCrmContactOperation } from '../operations/create-crm-contact-operation';

interface AddCrmContactDialogProps {
  visible: boolean;
  accountId: number | null;
  onHide: () => void;
  onSuccess: () => void;
}

const normalizeOptionalString = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const AddCrmContactDialog: React.FC<AddCrmContactDialogProps> = ({
  visible,
  accountId,
  onHide,
  onSuccess,
}) => {
  const [isPrimary, setIsPrimary] = useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [idNumber, setIdNumber] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const isValid = useMemo(
    () => firstName.trim().length > 0 && lastName.trim().length > 0,
    [firstName, lastName],
  );

  const reset = () => {
    setIsPrimary(false);
    setFirstName('');
    setLastName('');
    setIdNumber('');
    setEmail('');
    setPhone('');
    setAddress('');
    setNotes('');
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onHide();
  };

  const handleApprove = async () => {
    if (!accountId || !isValid || loading) return;

    await createCrmContactOperation(
      accountId,
      {
        isPrimary,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        idNumber: normalizeOptionalString(idNumber),
        email: normalizeOptionalString(email),
        phone: normalizeOptionalString(phone),
        address: normalizeOptionalString(address),
        notes: normalizeOptionalString(notes),
      },
      setLoading,
      () => {
        handleClose();
        onSuccess();
      },
    );
  };

  return (
    <Dialog
      header="הוספת איש קשר"
      visible={visible}
      onHide={handleClose}
      style={{ width: '520px', direction: 'rtl' }}
      modal
      closable={!loading}
      footer={
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}
        >
          <Button
            label="ביטול"
            icon="pi pi-times"
            severity="secondary"
            text
            onClick={handleClose}
            disabled={loading}
          />
          <Button
            label={loading ? 'שומר...' : 'אישור'}
            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            onClick={handleApprove}
            disabled={!isValid || loading || !accountId}
            loading={loading}
          />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Checkbox
            inputId="isPrimary"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(!!e.checked)}
            disabled={loading}
          />
          <label htmlFor="isPrimary" style={{ fontWeight: 600 }}>
            איש קשר ראשי
          </label>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
          }}
        >
          <div className="p-field">
            <label
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
              }}
            >
              שם פרטי *
            </label>
            <InputText
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={{ width: '100%' }}
              disabled={loading}
            />
          </div>

          <div className="p-field">
            <label
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 600,
              }}
            >
              שם משפחה *
            </label>
            <InputText
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              style={{ width: '100%' }}
              disabled={loading}
            />
          </div>
        </div>

        <div className="p-field">
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            תעודת זהות
          </label>
          <InputText
            value={idNumber}
            onChange={(e) => setIdNumber(e.target.value)}
            style={{ width: '100%' }}
            disabled={loading}
          />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
          }}
        >
          <div className="p-field">
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              אימייל
            </label>
            <InputText
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%' }}
              disabled={loading}
            />
          </div>
          <div className="p-field">
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              טלפון
            </label>
            <InputText
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={{ width: '100%' }}
              disabled={loading}
            />
          </div>
        </div>

        <div className="p-field">
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            כתובת
          </label>
          <InputText
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            style={{ width: '100%' }}
            disabled={loading}
          />
        </div>

        <div className="p-field">
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            הערות
          </label>
          <InputText
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ width: '100%' }}
            disabled={loading}
          />
        </div>
      </div>
    </Dialog>
  );
};
